<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Input, Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { getNotificationsContext } from '@client/components/notifications';
  import { SectionItem } from '@client/components/section';
  import { Select } from '@client/components/select';
  import { SignatureEditor } from '@client/components/signature-editor';
  import { SignatureFieldsList } from '@client/components/signature-fields-list';
  import { SignaturePreviewSettings } from '@client/components/signature-preview-settings';
  import { Spinner } from '@client/components/spinner';
  import { Step } from '@client/components/steps';
  import { Switch } from '@client/components/switch';

  import { SignaturesService } from '@client/services/signatures';
  import { TagBlocksService } from '@client/services/tag-blocks';
  import { UsersService } from '@client/services/users';

  import { userStore } from '@client/stores/user';

  import { extractActiveParams, getSignatureWarnings } from '@client/utils/signatures';
  import { userMetadata } from '@client/utils/users';

  import { SIGNATURE_WARNINGS, USER_REQUEST_PARAMS } from '@client/enums';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let updating = false;
  let signatureItems = [];
  let selectedSignature = null;
  let loadingPreview = false;
  let previewHtml = '';
  let currentStep = 0;

  // Editable signature fields
  let signatureName = '';
  let replyTemplateEnabled = false;
  let fields = [];
  let activeFields = [];
  let activeTextFields = [];
  let activeReplyFields = [];
  let activeReplyTextFields = [];
  let selectedTemplate = null;
  let showSource = false;

  // Template sources (editable copies)
  let htmltemplate = '';
  let texttemplate = '';
  let replyhtmltemplate = '';
  let replytexttemplate = '';

  // Original template sources (for comparison)
  let originalHtmltemplate = '';
  let originalTexttemplate = '';
  let originalReplyhtmltemplate = '';
  let originalReplytexttemplate = '';

  // Original values for change detection
  let originalSignatureName = '';
  let originalReplyTemplateEnabled = false;
  let originalFields = [];

  // Preview settings
  let users = [];
  let originalUsers = [];
  let selectedUser = { label: '', value: '' };
  let recipientEmail = $userStore.currentUser?.email || '';
  let actions = [];

  const processUsers = (data) => {
    return data.map((user) => {
      const username = user.email.split('@').shift();
      const meta = userMetadata({ ...user, features: $userStore.features });

      return {
        ...meta,
        username,
        label: meta.name,
        value: user.email
      };
    });
  };

  const getUsers = async () => {
    const response = await UsersService.getUsers({ ...USER_REQUEST_PARAMS, filterByDate: false, perPage: 100 });
    const processedUsers = processUsers(response?.data || []).sort((a, b) => a.label - b.label);
    originalUsers = [...processedUsers];
    users = [...processedUsers];

    // Pre-select a user after loading
    selectSender();
  };

  const selectSender = () => {
    const email = $userStore.currentUser?.email || 'adminuser';
    const username = email.split('@').shift();
    const findUser = users?.find(({ value }) => value.split('@').shift() === username);

    if (findUser) {
      selectedUser = { ...findUser };
    } else if (users.length) {
      selectedUser = { ...users[0] };
    } else {
      selectedUser = { label: '', value: email };
    }
  };

  const handleNext = async () => {
    if (currentStep === 0) {
      // Load users before transitioning to step 2
      if (!users.length) {
        await getUsers();
      } else {
        // Ensure user is selected if users already loaded
        selectSender();
      }
      currentStep = 1;
    }
  };

  const handleBack = () => {
    if (currentStep === 1) {
      currentStep = 0;
    }
  };

  const handleStepClick = async (index) => {
    if (index === 0) {
      currentStep = 0;
    }
    if (index === 1 && selectedSignature) {
      // Load users before transitioning to step 2
      if (!users.length) {
        await getUsers();
      } else {
        selectSender();
      }
      currentStep = 1;
    }
  };

  const getUserSignature = async (signature) => {
    if (!signature) return '';

    const data = {
      username: 'adminuser',
      scratchblockname: signature.rulename,
      testData: {
        testsender: 'adminuser'
      }
    };

    try {
      const { rendered } = await SignaturesService.getSignature(data);
      return rendered;
    } catch (error) {
      console.error('Failed to load signature preview:', error);
      return '';
    }
  };

  const handleSignatureSelect = async (event) => {
    selectedSignature = event.detail;

    if (selectedSignature) {
      // Set step data for workflow execution
      step.data.id = selectedSignature.rulename;
      step.data.rollback = { ...selectedSignature };
      // Initialize editable fields from selected signature
      signatureName = selectedSignature.rulename || '';
      replyTemplateEnabled = selectedSignature.replytemplateenabled || false;
      fields = [...(selectedSignature.params || [])];

      // Compute active fields from templates
      const sortedFields = fields.slice().sort((a, b) => a.name.localeCompare(b.name));
      const getActiveParams = extractActiveParams(selectedSignature.htmltemplate || '');
      const getActiveTextParams = extractActiveParams(selectedSignature.texttemplate || '');

      activeFields = sortedFields.map(({ name }) => getActiveParams.includes(name) && name).filter(Boolean);
      activeTextFields = sortedFields.map(({ name }) => getActiveTextParams.includes(name) && name).filter(Boolean);

      if (selectedSignature.replytemplateenabled) {
        const getActiveReplyParams = extractActiveParams(selectedSignature.replyhtmltemplate || '');
        activeReplyFields = sortedFields.map(({ name }) => getActiveReplyParams.includes(name) && name).filter(Boolean);

        if (selectedSignature.replytexttemplate) {
          const getActiveReplyTextParams = extractActiveParams(selectedSignature.replytexttemplate || '');
          activeReplyTextFields = sortedFields
            .map(({ name }) => getActiveReplyTextParams.includes(name) && name)
            .filter(Boolean);
        } else {
          activeReplyTextFields = [];
        }
      } else {
        activeReplyFields = [];
        activeReplyTextFields = [];
      }

      // Initialize template sources
      htmltemplate = selectedSignature.htmltemplate || '';
      texttemplate = selectedSignature.texttemplate || '';
      replyhtmltemplate = selectedSignature.replyhtmltemplate || '';
      replytexttemplate = selectedSignature.replytexttemplate || '';

      // Store original sources for comparison
      originalHtmltemplate = selectedSignature.htmltemplate || '';
      originalTexttemplate = selectedSignature.texttemplate || '';
      originalReplyhtmltemplate = selectedSignature.replyhtmltemplate || '';
      originalReplytexttemplate = selectedSignature.replytexttemplate || '';

      // Store original values for change detection
      originalSignatureName = selectedSignature.rulename || '';
      originalReplyTemplateEnabled = selectedSignature.replytemplateenabled || false;
      originalFields = JSON.stringify(selectedSignature.params || []);

      // Pre-select the first template option
      selectedTemplate = { label: 'Signature', value: 'htmltemplate' };

      loadingPreview = true;
      previewHtml = await getUserSignature(selectedSignature);
      loadingPreview = false;
    } else {
      // Reset step data
      step.data = {};

      previewHtml = '';
      signatureName = '';
      replyTemplateEnabled = false;
      fields = [];
      activeFields = [];
      activeTextFields = [];
      activeReplyFields = [];
      activeReplyTextFields = [];
      selectedTemplate = null;
      htmltemplate = '';
      texttemplate = '';
      replyhtmltemplate = '';
      replytexttemplate = '';
      originalHtmltemplate = '';
      originalTexttemplate = '';
      originalReplyhtmltemplate = '';
      originalReplytexttemplate = '';
      originalSignatureName = '';
      originalReplyTemplateEnabled = false;
      originalFields = [];
    }
  };

  const executeUpdate = async () => {
    updating = true;

    try {
      // Build updated signature object
      const updatedSignature = {
        ...selectedSignature,
        rulename: signatureName,
        replytemplateenabled: replyTemplateEnabled,
        params: fields,
        htmltemplate,
        texttemplate,
        replyhtmltemplate,
        replytexttemplate
      };

      const response = await TagBlocksService.save(updatedSignature);

      if (response.status >= 400) {
        notify($format('label.FAILURE.UPDATE_SIGNATURE', { values: { rulename: signatureName } }), 'error');
        throw new Error($format('label.FAILURE.UPDATE_SIGNATURE_FORM'));
      }

      notify($format('label.SUCCESS.UPDATE', { values: { item: signatureName } }));

      // Update id in case the name changed
      step.data.id = signatureName;

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.UPDATE_SIGNATURE', { values: { rulename: signatureName } }), 'error');
      throw error;
    } finally {
      updating = false;
    }
  };

  const rollbackUpdate = async () => {
    updating = true;

    try {
      const originalSignature = step.data.rollback;

      if (!originalSignature) {
        throw new Error($format('label.NO_ROLLBACK_DATA'));
      }

      const response = await TagBlocksService.save(originalSignature);

      if (response.status >= 400) {
        notify($format('label.FAILURE.ROLLBACK', { values: { item: originalSignature.rulename } }), 'error');
        throw new Error($format('label.FAILURE.ROLLBACK_SIGNATURE_UPDATE'));
      }

      notify($format('label.SUCCESS.ROLLBACK', { values: { item: originalSignature.rulename } }));

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.ROLLBACK_SIGNATURE_UPDATE'), 'error');
      throw error;
    } finally {
      updating = false;
    }
  };

  onMount(async () => {
    const signatures = await TagBlocksService.getAll();
    signatureItems = signatures
      .filter(({ archived }) => !archived)
      .map((signature) => ({
        label: signature.name || signature.rulename,
        value: signature.rulename,
        ...signature
      }));

    // Register execute and rollback functions with parent workflow
    if (onExecute) {
      onExecute(executeUpdate);
    }

    if (onRollback) {
      onRollback(rollbackUpdate);
    }
  });

  $: hasChanges =
    signatureName !== originalSignatureName ||
    replyTemplateEnabled !== originalReplyTemplateEnabled ||
    JSON.stringify(fields) !== originalFields ||
    htmltemplate !== originalHtmltemplate ||
    texttemplate !== originalTexttemplate ||
    replyhtmltemplate !== originalReplyhtmltemplate ||
    replytexttemplate !== originalReplytexttemplate;

  $: nextDisabled = currentStep < 1 || (currentStep === 1 && !hasChanges);

  // Compute warnings for current template
  $: currentTemplateWarnings = (() => {
    if (!selectedSignature || !selectedTemplate?.value) return [];

    const templateKey = selectedTemplate.value;
    const templateSources = {
      htmltemplate,
      texttemplate,
      replyhtmltemplate,
      replytexttemplate
    };

    const signatureForWarnings = {
      ...selectedSignature,
      params: fields,
      [templateKey]: templateSources[templateKey]
    };

    return getSignatureWarnings(signatureForWarnings, $userStore?.cname?.[1] || '', [$userStore.domain], templateKey);
  })();
  $: isSalesforce = $userStore.domain === 'salesforce.com';
  $: templateOptions = [
    { label: 'Signature', value: 'htmltemplate' },
    { label: 'Text Signature', value: 'texttemplate' },
    ...(replyTemplateEnabled
      ? [
          { label: 'Reply Signature', value: 'replyhtmltemplate' },
          { label: 'Reply Text Signature', value: 'replytexttemplate' }
        ]
      : [])
  ];
</script>

<div class="signature-update-form">
  <Step
    active={currentStep === 0}
    disabled={false}
    nextDisabled={!selectedSignature}
    title="Select Signature"
    summary={selectedSignature ? selectedSignature.label || selectedSignature.rulename : undefined}
    onNext={handleNext}
    onStep={() => handleStepClick(0)}
  >
    <SectionItem
      label={$format('label.SIGNATURE')}
      description={$format('label.SELECT_SIGNATURE_TO_UPDATE')}
      htmlFor="signatureSelect"
    >
      <Select
        id="signatureSelect"
        items={signatureItems}
        value={selectedSignature}
        on:change={handleSignatureSelect}
        placeholder={$format('label.SELECT_A_SIGNATURE_ELLIPSIS')}
        searchable
        showChevron
      >
        <div slot="empty" class="empty">
          {$format('label.NO_OPTIONS_AVAILABLE')}
        </div>
      </Select>
    </SectionItem>
    {#if selectedSignature}
      <SectionItem label="Preview" description="Preview of the selected signature">
        <div class="signature-preview">
          {#if loadingPreview}
            <div class="loading">
              <Spinner show inline />
            </div>
          {:else if previewHtml}
            <iframe
              tabindex="-1"
              style="width: 100%; border: none;"
              scrolling="no"
              title={selectedSignature.name || selectedSignature.rulename}
              srcdoc="<html><body><div style='transform: scale(.5,.5); transform-origin: top left; -moz-transform: scale(.5,.5); -moz-transform-origin: top left;'>{previewHtml}</div></body></html>"
            />
          {:else}
            <div class="no-preview">
              <p>No preview available</p>
            </div>
          {/if}
        </div>
      </SectionItem>
    {/if}
  </Step>

  <Step
    active={currentStep === 1}
    disabled={!selectedSignature}
    title="Update Signature"
    onBack={handleBack}
    onStep={() => handleStepClick(1)}
  >
    {#if selectedSignature}
      <SectionItem label="Name" description="The unique identifier for this signature" htmlFor="signatureName">
        <Input id="signatureName" type="text" bind:value={signatureName} placeholder="Enter signature name" />
      </SectionItem>
      <SectionItem label="Reply Signature" description="Enable a separate signature for email replies">
        <Switch bind:checked={replyTemplateEnabled} />
      </SectionItem>
      <SectionItem label="Preview Settings" description="Configure preview options for this signature">
        <SignaturePreviewSettings bind:users bind:originalUsers bind:selectedUser bind:recipientEmail bind:actions />
      </SectionItem>
      <SectionItem label="Fields" description="Manage the fields available for this signature">
        <SignatureFieldsList
          bind:fields
          {isSalesforce}
          {activeFields}
          {activeTextFields}
          {activeReplyFields}
          {activeReplyTextFields}
        />
      </SectionItem>
      <SectionItem label="Template" description="Select which template to edit" htmlFor="templateSelect">
        <Select
          id="templateSelect"
          items={templateOptions}
          bind:value={selectedTemplate}
          placeholder={$format('label.SELECT_A_SIGNATURE_ELLIPSIS')}
          showChevron
          clearable={false}
        />
      </SectionItem>
      <div class="edit-wrapper">
        {#if currentTemplateWarnings.length}
          <div class="warnings">
            {#each currentTemplateWarnings as { type, data }}
              <div
                class="warning"
                id="warnings-{type}-{selectedSignature._id}"
                style="border-color: {SIGNATURE_WARNINGS[type]?.color}; color: {SIGNATURE_WARNINGS[type]?.color};"
              >
                <Icon name={SIGNATURE_WARNINGS[type]?.icon} size={SIGNATURE_WARNINGS[type]?.iconSize} />
              </div>
              <Tooltip class="warnings-tooltip" target="warnings-{type}-{selectedSignature._id}">
                <div class="warning-tooltip-content">
                  <b>{$format(SIGNATURE_WARNINGS[type]?.label)} ({data.length})</b>
                  <ul>
                    {#each data as item}
                      <li>{item}</li>
                    {/each}
                  </ul>
                </div>
              </Tooltip>
            {/each}
          </div>
        {/if}
        {#if selectedTemplate?.value === 'htmltemplate'}
          <SignatureEditor
            bind:data={selectedSignature}
            bind:source={htmltemplate}
            bind:fields
            bind:selectedUser
            bind:recipientEmail
            bind:actions
            bind:showSource
            originalSource={originalHtmltemplate}
          />
        {:else if selectedTemplate?.value === 'texttemplate'}
          <SignatureEditor
            bind:data={selectedSignature}
            bind:source={texttemplate}
            bind:fields
            bind:selectedUser
            bind:recipientEmail
            bind:actions
            bind:showSource
            originalSource={originalTexttemplate}
            textOnly={true}
            editLabel="label.EDIT_TEXT"
          />
        {:else if selectedTemplate?.value === 'replyhtmltemplate'}
          <SignatureEditor
            bind:data={selectedSignature}
            bind:source={replyhtmltemplate}
            bind:fields
            bind:selectedUser
            bind:recipientEmail
            bind:actions
            bind:showSource
            originalSource={originalReplyhtmltemplate}
          />
        {:else if selectedTemplate?.value === 'replytexttemplate'}
          <SignatureEditor
            bind:data={selectedSignature}
            bind:source={replytexttemplate}
            bind:fields
            bind:selectedUser
            bind:recipientEmail
            bind:actions
            bind:showSource
            originalSource={originalReplytexttemplate}
            textOnly={true}
            editLabel="label.EDIT_TEXT"
          />
        {/if}
      </div>
    {/if}
  </Step>
</div>

<style lang="scss">
  .signature-update-form {
    padding: var(--padding-base);

    :global {
      .svelte-select-list {
        z-index: 9999 !important;
      }
    }
  }

  .empty {
    color: var(--color-gray-600);
    padding: var(--padding-xlarge);
    text-align: center;
  }

  .signature-preview {
    background-color: var(--color-white);
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius-base);
    height: fit-content;
    min-height: 100px;
    padding: var(--padding-small);

    iframe {
      min-height: 100px;
    }

    .loading {
      align-items: center;
      display: flex;
      justify-content: center;
      min-height: 100px;
      padding: var(--padding-large);
    }

    .no-preview {
      align-items: center;
      color: var(--color-gray-600);
      display: flex;
      justify-content: center;
      min-height: 100px;
      padding: var(--padding-base);

      p {
        margin: 0;
      }
    }
  }

  .edit-wrapper {
    margin-top: calc(var(--margin-large) + 6px);

    :global {
      .editor .preview {
        border: 1px solid var(--border-color) !important;
      }
    }
  }

  .warnings {
    align-items: center;
    display: flex;
    gap: 8px;
    margin-bottom: var(--margin-base);

    .warning {
      align-items: center;
      border: 1px solid;
      border-radius: 4px;
      cursor: pointer;
      display: flex;
      height: 24px;
      justify-content: center;
      width: 24px;

      i {
        font-size: 14px;
      }
    }
  }

  :global(.warning-tooltip-content) {
    text-align: left;

    ul {
      list-style: disc;
      margin: var(--margin-small) 0 0 var(--margin-large);
      max-height: 200px;
      overflow-y: auto;
      padding: 0;
    }
  }
</style>
